import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from '@/lib/i18n';
import { Button } from '@/components/ui';
import { apiClient } from '@/lib/apiClient';
import type { OrderDetail } from '@/lib/api-admin/types';
import { fetchDocument, type PrintDocumentData } from './usePrintDocument';
import type { PrintDocumentKind } from './types';
import { TransactionStatementDocument } from './documents/TransactionStatementDocument';
import { OrderStatementDocument } from './documents/OrderStatementDocument';
import './printDocument.css';

/** 한 번에 출력할 수 있는 주문 수 — 주문마다 상세·출력물 두 번씩 부르므로 너무 많으면 느려진다. */
export const BULK_PRINT_MAX = 100;

interface BulkPrintResult {
  docs: PrintDocumentData[];
  /** 불러오지 못한 주문(목록에서 고른 순서의 주문 id). */
  failed: string[];
}

const fetchOrder = async (orderId: string): Promise<OrderDetail> => {
  const [data] = await apiClient.request('get', '/admin/v1/orders/{orderId}', {
    params: { path: { orderId } },
  });
  return data as OrderDetail;
};

/** 고른 순서대로 주문 상세 → 출력물. 한 건이 실패해도 나머지는 출력한다. */
const fetchAll = async (orderIds: ReadonlyArray<string>, kind: PrintDocumentKind): Promise<BulkPrintResult> => {
  const docs: PrintDocumentData[] = [];
  const failed: string[] = [];
  for (const orderId of orderIds) {
    try {
      const order = await fetchOrder(orderId);
      docs.push(await fetchDocument(orderId, kind, order));
    } catch {
      failed.push(orderId);
    }
  }
  return { docs, failed };
};

/**
 * QA-455 — 주문 목록에서 고른 주문의 주문내역서·거래명세서를 한 번에 미리보고 인쇄한다(주문마다 새 쪽).
 * 문서 모양은 주문 상세 [주문내역서]/[거래명세서]와 같다.
 */
export const BulkPrintDocumentModal = ({
  orderIds,
  kind,
  onClose,
}: {
  orderIds: ReadonlyArray<string>;
  kind: PrintDocumentKind;
  onClose: () => void;
}) => {
  const { t } = useTranslation('orders');
  const ids = orderIds.slice(0, BULK_PRINT_MAX);
  const query = useQuery<BulkPrintResult>({
    queryKey: ['admin', 'order-print-batch', kind, ids],
    queryFn: () => fetchAll(ids, kind),
    staleTime: 0,
    gcTime: 0,
  });
  const title = t('orders.print.bulkTitle', {
    kind: t(`orders.print.kind.${kind}`),
    count: ids.length,
    defaultValue: '{{kind}} 일괄 출력 ({{count}}건)',
  });

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="print-root fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4"
      style={{ background: 'var(--scrim-modal)' }}
      onClick={onClose}
    >
      <div
        className="my-8 w-full max-w-3xl rounded-xl shadow-[var(--elevation-3)]"
        style={{ background: 'var(--surface-0)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="print-hide flex items-center justify-between border-b px-6 py-4" style={{ borderColor: 'var(--divider-soft)' }}>
          <h2 className="text-lg font-semibold" style={{ color: 'var(--content-strong)' }}>
            {title}
          </h2>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="cta"
              size="sm"
              onClick={() => window.print()}
              disabled={query.isPending || !query.data || query.data.docs.length === 0}
              data-testid="bulk-print-run"
            >
              {t('orders.print.printButton')}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              {t('orders.print.close')}
            </Button>
          </div>
        </div>

        <div className="px-6 py-6">
          {orderIds.length > BULK_PRINT_MAX ? (
            <p className="print-hide mb-3 text-[12px]" style={{ color: 'var(--status-warning-fg, var(--content-muted))' }}>
              {t('orders.print.bulkLimit', {
                max: BULK_PRINT_MAX,
                defaultValue: '한 번에 {{max}}건까지 출력합니다. 앞의 {{max}}건만 담았습니다.',
              })}
            </p>
          ) : null}
          {query.isPending ? (
            <p className="print-hide py-8 text-center text-sm" style={{ color: 'var(--content-muted)' }}>
              {t('orders.print.loading')}
            </p>
          ) : query.isError || !query.data ? (
            <p className="print-hide py-8 text-center text-sm" role="alert" style={{ color: 'var(--status-danger-fg)' }}>
              {t('orders.print.error')}
            </p>
          ) : (
            <>
              {query.data.failed.length > 0 ? (
                <p className="print-hide mb-3 text-[12px]" role="alert" style={{ color: 'var(--status-danger-fg)' }} data-testid="bulk-print-failed">
                  {t('orders.print.bulkFailed', {
                    count: query.data.failed.length,
                    defaultValue: '{{count}}건은 불러오지 못해 빠졌습니다.',
                  })}
                </p>
              ) : null}
              <div className="print-batch flex flex-col gap-6" data-testid="bulk-print-batch">
                {query.data.docs.map((d, i) =>
                  d.kind === 'transaction-statement' ? (
                    <TransactionStatementDocument key={i} doc={d.doc} />
                  ) : (
                    <OrderStatementDocument key={i} doc={d.doc} />
                  ),
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};
